import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Checkbox } from '@upmc/ims.component-library';

import { Headline5 } from '../../../components/UI/MDC/Typography/Typography';
import Icon from '../../../components/UI/Icon/Icon';
import classes from '../UserAccessReview.module.scss';
import * as actions from '../../../store/uar/actions';
import * as utils from '../../../utils';
import StyleAuxiliary from '../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import ServiceAccountAppDetails from './ServiceAccountAppDetails';

const AccountDetailData = props => {
	/**
	 * Extract needed variables from the props provided by the `Scaffold`
	 */
	const {
		dispatch,
		canPerformActions,
		readOnly,
		appModifications,
		trackingNumber,
		proceedToCheckoutHandler,
		removeFromCartHandler,
		resetCart,
		modifyHandler,
		revokeHandler,
		modifyAndValidateHandler,
		profile,
		setProfile,
		applications,
		mobileDevices,
		badges,
		serviceAccounts,
		bulkSelections,
	} = props;

	const environment = process.env.REACT_APP_IMS_ENV;

	const isInCart = appId => {
		return appModifications[appId];
	};

	const expandRole = (e, id) => {
		let newApplications = [...applications];
		newApplications.forEach(app => {
			if (app.id === id) {
				app.showFull = app.showFull ? !app.showFull : true;
			}
		});
		setProfile({ ...profile, applications: newApplications });
	};

	const addBulkSelection = (event, member) => {
		//update global store
		if (event.target.checked) {
			dispatch(
				actions.addBulkSelection({
					payload: member,
				})
			);
		} else {
			dispatch(
				actions.removeBulkSelection({
					payload: member,
				})
			);
		}
	};

	/**
	 * when component mounts, reset the cart state inherited from AccountDetails.js
	 */
	useEffect(() => {
		if (resetCart) {
			resetCart();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Retrieves justification message if application ineligible for removal.
	 * @param {Number} id application unique identifier.
	 * @returns {String} nullable restriction justification message.
	 */
	const getUnremovableAppMessage = id => {
		switch (id) {
			case 184: //CCHIE dbMotion
				return 'Mandatory In Conjunction With Clinical Applications.';
			case 295: //Kronos
				return 'Mandatory For All Employees (Enables Employee To Have Access to Their Time Card)';
			case 296: //MyHub
				return 'Mandatory For All Employees (Works for HR Direct Purposes)';
			case 297: //IMS
				return 'Mandatory For All Users.';
			default:
				return '';
		}
	};

	/**
	 * Determines whether target application is ineligible for removal.
	 * @param {Number} id application unique identifier.
	 * @returns {boolean} whether application is ineligible for removal.
	 */
	const isUnremovableApp = id => {
		const exemptions = [
			184, //CCHIE dbMotion
			295, //Kronos
			296, //MyHub
			297, //IMS
		];
		return exemptions.includes(id);
	};

	const toggleApps = id => {
		const newProfile = { ...profile };
		const accountToUpdate = newProfile.serviceAccounts.filter(p => {
			return p.esoFiId === id;
		});
		accountToUpdate.forEach(a => {
			a.displayApps = !a.displayApps;
		});

		setProfile(newProfile);
	};

	const getApplicationDetailsLink = id => {
		if (!id) {
			id = 0;
		}
		switch (environment.toUpperCase()) {
			case 'PRODUCTION':
				return (
					'https://applications.upmc.com/ApplicationDetails.aspx?appId=' +
					id
				);
			case 'TEST':
				return (
					'https://testimsapps.upmc.com/ApplicationDetails.aspx?appId=' +
					id
				);
			default:
				return (
					'https://testimsapps.upmc.com/ApplicationDetails.aspx?appId=' +
					id
				);
		}
	};

	const generateAppSummary = () => {
		return (
			<section
				className={utils.concatenateClassNames(
					['profile__container--vertical'],
					classes
				)}
			>
				{Object.keys(appModifications).map(index => {
					const mod = appModifications[index];
					return (
						<div
							className={utils.concatenateClassNames(
								['profile__item--application'],
								classes
							)}
							key={mod.data.id}
						>
							<div
								className={utils.concatenateClassNames(
									['profile__container--application'],
									classes
								)}
							>
								<div
									className={utils.concatenateClassNames(
										[
											'profile__item',
											'profile__application--title',
										],
										classes
									)}
								>
									{mod.data.name}
								</div>
								<div
									className={utils.concatenateClassNames(
										[
											'profile__item',
											'profile__application--role',
										],
										classes
									)}
								>
									{mod.action}
								</div>
							</div>
						</div>
					);
				})}
				<section
					className={utils.concatenateClassNames(
						['profile__container--vertical-centered'],
						classes
					)}
				>
					{/* <Link
                        to={{
                            pathname: '/RRS/Request_ModifyDetail_View.aspx',
                            search: '?tn=' + trackingNumber
                        }}> */}
					<Button
						startIcon={<Icon icon='shopping_cart' />}
						disabled={Object.keys(appModifications).length === 0}
						onClick={modifyAndValidateHandler}
					>
						Modify and Validate
					</Button>
					{/* </Link> */}
				</section>
			</section>
		);
	};

	const generateAppRoleDescription = app => {
		if (app.id === 79) {
			const epicDescription = app.role.split(';');
			return (
				<span>
					{epicDescription.map(role => {
						const kvp = role.split(':');
						if (kvp.length === 2) {
							return (
								<div>
									<u>{kvp[0]}</u>: {kvp[1]}
								</div>
							);
						} else {
							return <div>{role}</div>;
						}
					})}
				</span>
			);
		} else {
			return app.role.length > 100 ? (
				app.showFull ? (
					<span>
						{app.role}{' '}
						<span
							onClick={e => expandRole(e, app.id)}
							className={utils.concatenateClassNames(
								['link'],
								classes
							)}
						>
							(see less)
						</span>
					</span>
				) : (
					<span>
						{app.role.substring(0, 86) + '... '}{' '}
						<span
							onClick={e => expandRole(e, app.id)}
							className={utils.concatenateClassNames(
								['link'],
								classes
							)}
						>
							(see more)
						</span>
					</span>
				)
			) : (
				app.role
			);
		}
	};

	const generateAppList = (svcAcctEsoId = null) => {
		return (
			<>
				{!readOnly ? (
					<section
						className={utils.concatenateClassNames(
							['profile__container--vertical'],
							classes
						)}
					>
						<Button
							variant='outlined'
							startIcon={<Icon icon='shopping_cart' />}
							disabled={
								Object.keys(appModifications).length === 0
							}
							onClick={proceedToCheckoutHandler}
						>
							{`Proceed To Checkout.  (${
								Object.keys(appModifications).length
							} items in cart)`}
						</Button>
					</section>
				) : null}
				<section
					className={utils.concatenateClassNames(
						['profile__container--vertical'],
						classes
					)}
				>
					{applications && applications.length > 0 ? (
						applications.map(app => {
							return (
								<div
									className={utils.concatenateClassNames(
										['profile__item--application'],
										classes
									)}
									key={app.id}
								>
									<div
										className={utils.concatenateClassNames(
											['profile__container--application'],
											classes
										)}
									>
										<div
											className={utils.concatenateClassNames(
												[
													'profile__item',
													'profile__application--title',
												],
												classes
											)}
										>
											<a
												href={getApplicationDetailsLink(
													app.appViewerId
												)}
												target='_blank'
												rel='noopener noreferrer'
											>
												{app.name}
											</a>
										</div>
										<div
											className={utils.concatenateClassNames(
												[
													'profile__item',
													'profile__application--username',
												],
												classes
											)}
										>
											{app.username}
										</div>
										<div
											className={utils.concatenateClassNames(
												[
													'profile__item',
													'profile__application--role',
												],
												classes
											)}
										>
											{app.role &&
												generateAppRoleDescription(app)}
										</div>
										{isUnremovableApp(app.id) ? (
											<div
												className={utils.concatenateClassNames(
													[
														'profile__item',
														'profile__application--action__container',
													],
													classes
												)}
											>
												<StyleAuxiliary>
													{getUnremovableAppMessage(
														app.id
													)}
												</StyleAuxiliary>
											</div>
										) : null}
										{!readOnly ? (
											isInCart(app.id) ? (
												<>
													<div
														className={utils.concatenateClassNames(
															[
																'profile__item',
																'profile__application--action__container',
															],
															classes
														)}
													>
														<Button
															variant='outlined'
															onClick={() =>
																removeFromCartHandler(
																	app.id
																)
															}
														>
															Remove From Cart
														</Button>
													</div>
												</>
											) : isUnremovableApp(
													app.id
											  ) ? null : app.pendingRequest ? (
												<div
													className={utils.concatenateClassNames(
														[
															'profile__item',
															'profile__application--action__container',
														],
														classes
													)}
												>
													<StyleAuxiliary>
														Pending Request
													</StyleAuxiliary>
												</div>
											) : (
												<StyleAuxiliary
													classes={utils.concatenateClassNames(
														[
															'profile__application--action__container',
														],
														classes
													)}
												>
													<div
														className={utils.concatenateClassNames(
															[
																'profile__item',
																'profile__application--button',
															],
															classes
														)}
													>
														<Button
															variant='outlined'
															onClick={() =>
																modifyHandler(
																	app.id
																)
															}
															disabled={
																!canPerformActions
															}
														>
															Modify
														</Button>
													</div>
													<div
														className={utils.concatenateClassNames(
															[
																'profile__item',
																'profile__application--button',
															],
															classes
														)}
													>
														<Button
															variant='outlined'
															onClick={() =>
																revokeHandler(
																	app.id
																)
															}
															disabled={
																!canPerformActions
															}
														>
															Revoke
														</Button>
													</div>
												</StyleAuxiliary>
											)
										) : null}
									</div>
								</div>
							);
						})
					) : (
						<div
							className={utils.concatenateClassNames(
								['profile__item--application'],
								classes
							)}
						>
							<div
								className={utils.concatenateClassNames(
									['profile__container--application'],
									classes
								)}
							>
								<div
									className={utils.concatenateClassNames(
										[
											'profile__item',
											'profile__application--title',
										],
										classes
									)}
								>
									No Applications
								</div>
							</div>
						</div>
					)}
				</section>
			</>
		);
	};

	return (
		<>
			<section
				className={utils.concatenateClassNames(
					['profile--title', 'text--center'],
					classes
				)}
			>
				<Headline5>Apps</Headline5>
			</section>
			{trackingNumber && trackingNumber.length > 0
				? generateAppSummary()
				: generateAppList()}

			<section
				className={utils.concatenateClassNames(
					['profile--title', 'text--center'],
					classes
				)}
			>
				<Headline5>Mobile Devices</Headline5>
			</section>
			<section
				className={utils.concatenateClassNames(
					['profile__container--vertical'],
					classes
				)}
			>
				{mobileDevices && mobileDevices.length > 0 ? (
					mobileDevices.map(dev => {
						return (
							<div
								className={utils.concatenateClassNames(
									['profile__item--application'],
									classes
								)}
								key={dev.number}
							>
								<div
									className={utils.concatenateClassNames(
										['profile__container--application'],
										classes
									)}
								>
									<div
										className={utils.concatenateClassNames(
											[
												'profile__item',
												'profile__application--title',
											],
											classes
										)}
									>
										{dev.number}
									</div>
									<div
										className={utils.concatenateClassNames(
											[
												'profile__item',
												'profile__application--role',
											],
											classes
										)}
									>
										{dev.type}
									</div>
								</div>
							</div>
						);
					})
				) : (
					<div
						className={utils.concatenateClassNames(
							['profile__item--application'],
							classes
						)}
					>
						<div
							className={utils.concatenateClassNames(
								['profile__container--application'],
								classes
							)}
						>
							<div
								className={utils.concatenateClassNames(
									[
										'profile__item',
										'profile__application--title',
									],
									classes
								)}
							>
								No Mobile Devices
							</div>
						</div>
					</div>
				)}
			</section>

			<section
				className={utils.concatenateClassNames(
					['profile--title', 'text--center'],
					classes
				)}
			>
				<Headline5>Badges</Headline5>
			</section>
			<section
				className={utils.concatenateClassNames(
					['profile__container--vertical'],
					classes
				)}
			>
				{badges && badges.length > 0 ? (
					badges.map(badge => {
						return (
							<div
								className={utils.concatenateClassNames(
									['profile__item--application'],
									classes
								)}
								key={badge.number}
							>
								<div
									className={utils.concatenateClassNames(
										['profile__container--application'],
										classes
									)}
								>
									<div
										className={utils.concatenateClassNames(
											[
												'profile__item',
												'profile__application--title',
											],
											classes
										)}
									>
										{badge.type}
									</div>
									<div
										className={utils.concatenateClassNames(
											[
												'profile__item',
												'profile__application--role',
											],
											classes
										)}
									>
										{badge.number}
									</div>
									<div
										className={utils.concatenateClassNames(
											[
												'profile__item',
												'profile__application--role',
											],
											classes
										)}
									>
										{badge.accessLocations}
									</div>
								</div>
							</div>
						);
					})
				) : (
					<div
						className={utils.concatenateClassNames(
							['profile__item--application'],
							classes
						)}
					>
						<div
							className={utils.concatenateClassNames(
								['profile__container--application'],
								classes
							)}
						>
							<div
								className={utils.concatenateClassNames(
									[
										'profile__item',
										'profile__application--title',
									],
									classes
								)}
							>
								No Badges
							</div>
						</div>
					</div>
				)}
			</section>

			<section
				className={utils.concatenateClassNames(
					['profile--title', 'text--center'],
					classes
				)}
			>
				<Headline5>Service Accounts</Headline5>
			</section>
			<section
				className={utils.concatenateClassNames(
					['profile__container--vertical'],
					classes
				)}
			>
				{serviceAccounts && serviceAccounts.length > 0 ? (
					serviceAccounts.map(account => {
						return (
							<div
								className={utils.concatenateClassNames(
									['profile__item--application'],
									classes
								)}
								key={account.eso}
							>
								<div
									className={utils.concatenateClassNames(
										['profile__container--application'],
										classes
									)}
								>
									{canPerformActions && (
										<div
											className={
												classes['user-detail-select']
											}
										>
											<Checkbox
												name={'bulk_' + account.eso}
												label='Select account for bulk action'
												checked={account.selected}
												onClick={e => {
													addBulkSelection(
														e,
														account
													);
												}}
											/>
										</div>
									)}
									<div
										className={utils.concatenateClassNames(
											[
												'profile__item',
												'profile__service-account--role',
											],
											classes
										)}
									>
										<div>
											<span
												className={
													classes[
														'profile__service-account--title'
													]
												}
											>
												UPMC E-Sign-On ID:{' '}
											</span>
											{account.eso}
										</div>
										<div>
											<span
												className={
													classes[
														'profile__service-account--title'
													]
												}
											>
												NTID:{' '}
											</span>
											{account.ntid ?? 'N/A'}
										</div>
									</div>
									<div
										className={utils.concatenateClassNames(
											[
												'profile__item',
												'profile__service-account--role',
											],
											classes
										)}
									>
										<span
											className={
												classes[
													'profile__service-account--title'
												]
											}
										>
											Description:{' '}
										</span>
										{account.description}
									</div>
									<div
										className={utils.concatenateClassNames(
											[
												'profile__item',
												'profile__service-account--role',
											],
											classes
										)}
									>
										<div>
											<span
												className={
													classes[
														'profile__service-account--title'
													]
												}
											>
												Date Validated:{' '}
											</span>
											{utils.prettyDateFormat(
												account.dateLastValidated
											)}
										</div>
										<div>
											<span
												className={
													classes[
														'profile__service-account--title'
													]
												}
											>
												Date Validation Due:{' '}
											</span>
											{utils.prettyDateFormat(
												account.dateValidationDue
											)}
										</div>
									</div>
								</div>
								<div
									className={utils.concatenateClassNames(
										[
											'profile__container--application',
											'profile__container--button-tray-right',
										],
										classes
									)}
								>
									<div
										className={utils.concatenateClassNames(
											[
												'profile__item',
												'profile__item--no-pad',
											],
											classes
										)}
									>
										<Button
											variant='outlined'
											onClick={() =>
												toggleApps(account.esoFiId)
											}
										>
											View Applications
										</Button>
									</div>
									{!readOnly ? (
										<>
											<div
												className={utils.concatenateClassNames(
													[
														'profile__item',
														'profile__item--no-pad',
													],
													classes
												)}
											>
												<Link
													to={{
														pathname:
															'/UserAccessReview/AccountDetails',
														search:
															'?esoFiId=' +
															account.esoFiId,
													}}
												>
													<Button
														variant='outlined'
														disabled={
															!canPerformActions
														}
													>
														Modify/Revoke
														Applications
													</Button>
												</Link>
											</div>
											{account.administrative ? null : (
												<div
													className={utils.concatenateClassNames(
														[
															'profile__item',
															'profile__item--no-pad',
														],
														classes
													)}
												>
													<Link
														to={{
															pathname:
																'/UserAccessReview/Reassign',
															search:
																'?esoFiId=' +
																account.esoFiId,
														}}
													>
														<Button
															variant='outlined'
															disabled={
																!canPerformActions
															}
														>
															Reassign
														</Button>
													</Link>
												</div>
											)}
											<div
												className={utils.concatenateClassNames(
													[
														'profile__item',
														'profile__item--no-pad',
													],
													classes
												)}
											>
												<Link
													to={{
														pathname:
															'/UserAccessReview/Terminate',
														search:
															'?esoFiId=' +
															account.esoFiId,
													}}
												>
													<Button
														variant='outlined'
														disabled={
															!canPerformActions
														}
													>
														Terminate
													</Button>
												</Link>
											</div>
										</>
									) : null}
								</div>
								<ServiceAccountAppDetails account={account} />
							</div>
						);
					})
				) : (
					<div
						className={utils.concatenateClassNames(
							['profile__item--application'],
							classes
						)}
					>
						<div
							className={utils.concatenateClassNames(
								['profile__container--application'],
								classes
							)}
						>
							<div
								className={utils.concatenateClassNames(
									[
										'profile__item',
										'profile__application--title',
									],
									classes
								)}
							>
								No Service Accounts
							</div>
						</div>
					</div>
				)}
				{!readOnly && serviceAccounts && serviceAccounts.length > 0 ? (
					<div
						className={utils.concatenateClassNames(
							['profile__item--application'],
							classes
						)}
					>
						<div
							className={utils.concatenateClassNames(
								['profile__container--application'],
								classes
							)}
						>
							<div
								className={utils.concatenateClassNames(
									['profile__item'],
									classes
								)}
							>
								<Link
									to={{
										pathname:
											'/UserAccessReview/Reassign',
									}}
									onClick={e => {
										if (bulkSelections.length === 0) {
											e.preventDefault();
										}
									}}
								>
									<Button disabled={!canPerformActions}>
										Reassign All Selected
									</Button>
								</Link>
							</div>
							<div
								className={utils.concatenateClassNames(
									['profile__item'],
									classes
								)}
							>
								<Link
									to={{
										pathname:
											'/UserAccessReview/Terminate',
									}}
									onClick={e => {
										if (bulkSelections.length === 0) {
											e.preventDefault();
										}
									}}
								>
									<Button disabled={!canPerformActions}>
										Terminate All Selected
									</Button>
								</Link>
							</div>
						</div>
					</div>
				) : null}
			</section>
		</>
	);
};

export default AccountDetailData;
